/**
 * The reporting currency on screen (28/10/2026) — the one currency every
 * tracker's money is shown in (Settings › Trackers, US dollars by default).
 * Set from GET /app-env as soon as it loads (useAppEnv); read synchronously by
 * pure formatters, or through useReportingCurrency by state that must follow
 * it once it arrives (a stats tab opened before /app-env answered).
 * SMSWiser's route prices and the estimates built on them are US dollars as
 * SMSWiser gives them, never converted — they are written with `usd`.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useSyncExternalStore } from 'react';
import { DEFAULT_REPORTING_CURRENCY, formatMoney, isReportingCurrency } from '@trafficpulse/shared';

let current: string = DEFAULT_REPORTING_CURRENCY;
const listeners = new Set<() => void>();

export const reportingCurrency = (): string => current;

export function setReportingCurrency(code: unknown): void {
  if (!isReportingCurrency(code) || code === current) return;
  current = code;
  listeners.forEach((listener) => listener());
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => void listeners.delete(listener);
};

/** The reporting currency, re-rendering when /app-env (or a change in Settings) sets it. */
export const useReportingCurrency = (): string => useSyncExternalStore(subscribe, reportingCurrency, reportingCurrency);

/** Tracker money: "$18", "-$10,803.61" — in the reporting currency. */
export const money = (value: number, decimals = 2): string => formatMoney(value, decimals, current);

/** SMSWiser route prices and estimates: always US dollars, as SMSWiser gives them. */
export const usd = (value: number, decimals = 2): string => formatMoney(value, decimals, 'USD');
